import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { IMAGES, WHATSAPP_URL } from "../data/content";

const LINES = ["Exceptional Food.", "Thoughtful Presentation.", "Memorable Experiences."];

const container = { animate: { transition: { staggerChildren: 0.15, delayChildren: 0.4 } } };
const line = {
  initial: { y: "110%" },
  animate: { y: 0, transition: { ease: [0.76, 0, 0.24, 1], duration: 1.2 } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const go = (hash) => {
    const el = document.querySelector(hash);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="noise relative min-h-screen flex items-center overflow-hidden pt-[70px]">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 75% 40%, rgba(212,175,55,0.08), transparent 70%)" }} />
      <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center py-16">
        <motion.div variants={container} initial="initial" animate="animate" style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p variants={line} data-testid="hero-overline" className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-8">
            Novaphi Catering &amp; Events — South Africa
          </motion.p>
          <h1 data-testid="hero-heading" className="font-serif-e font-medium text-5xl sm:text-6xl md:text-7xl lg:text-[5.2rem] leading-[1.02] tracking-tighter text-[#F9F9F6]">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span variants={line} className="block">
                  {i === 1 ? <em className="text-[#D4AF37] not-italic font-light italic">{l}</em> : l}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p variants={line} data-testid="hero-subtext" className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#F9F9F6]/70">
            Professional catering for corporate events, private functions and celebrations — from a boardroom for ten to an event for 150 guests, to an intimate cheeseboard for two.
          </motion.p>
          <motion.div variants={line} className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => go("#contact")} data-testid="hero-quote-btn"
                    className="rounded-full bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] transition-colors">
              Request a Quote
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
               className="rounded-full border border-[#F9F9F6]/30 text-[#F9F9F6] px-8 py-4 text-xs uppercase tracking-[0.2em] flex items-center gap-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <MessageCircle size={15} strokeWidth={1.5} /> WhatsApp Us
            </a>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5 relative">
          <div className="relative aspect-[3/4] max-h-[75vh] w-full overflow-hidden rounded-t-[160px] vignette border border-[#D4AF37]/30">
            <motion.img src={IMAGES.chef} alt="Novaphi chef in her kitchen" data-testid="hero-chef-image"
                        style={{ y: imgY }} initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }}
                        className="w-full h-[118%] object-cover object-top" />
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
                      className="absolute -bottom-5 -left-5 md:-left-10 bg-[#0A0A0A] border border-[#D4AF37]/40 px-6 py-4">
            <p className="font-serif-e text-3xl text-[#D4AF37]">150<span className="text-lg align-top">+</span></p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#F9F9F6]/60 mt-1">Guests catered per event</p>
          </motion.div>
        </motion.div>
      </div>
      <motion.button onClick={() => go("#manifesto")} data-testid="hero-scroll-cue" aria-label="Scroll down"
                     animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}
                     className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#F9F9F6]/50 hover:text-[#D4AF37] transition-colors">
        <ArrowDown size={20} strokeWidth={1} />
      </motion.button>
    </section>
  );
}
